import { AgGridAngular } from 'ag-grid-angular';
import {
    AllCommunityModule,
    ClientSideRowModelModule,
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
    IRowNode,
    ModuleRegistry,
} from 'ag-grid-community';
import {
    ClipboardModule,
    ContextMenuModule,
    MasterDetailModule,
} from 'ag-grid-enterprise';
import { AG_GRID_LOCALE_EN } from '@ag-grid-community/locale';
import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnInit,
    inject,
} from '@angular/core';
import { PushPipe } from '@ngrx/component';
import { AG_GRID_LOCALES } from '@wdx/shared/components/ag-grid';
import { WdxRadioButtonGroupModule } from '@wdx/shared/components/wdx-radio-button-group';
import { WdxSpinnerModule } from '@wdx/shared/components/wdx-spinner';
import {
    FriendlyProblemDetails,
    InvestmentDataColumnConfiguration,
    InvestmentDataConfiguration,
    InvestmentDataSectionConfiguration,
    PerformancePeriod,
    SortDirection,
    WdxAgGridThemeDirective,
} from '@wdx/shared/utils';
import { forkJoin, map, Observable, take } from 'rxjs';
import { PortfolioConfigName } from '../constants/portfolio-config-names.const';
import { Transaction, TransactionType } from '../models';
import { PortfolioNotLoadedComponent } from '../portfolio-not-loaded/portfolio-not-loaded.component';
import { PortfolioAgGridColumnMapperService } from '../services/portfolio-ag-grid-column-mapper.service';
import { PortfolioConfigService } from '../services/portfolio-config.service';
import {
    PortfolioDetailCellRendererComponent,
    PortfolioDetailRendererParams,
} from '../shared/portfolio-detail-renderer/portfolio-detail-renderer.component';
import {
    TRANSACTION_DEFAULT_PRIMARY_COLUMNS,
    TRANSACTION_DETAIL_DEFAULT_FIELDS,
} from './transactions-default-configs.const';

ModuleRegistry.registerModules([
    AllCommunityModule,
    ClientSideRowModelModule,
    MasterDetailModule,
    ClipboardModule,
    ContextMenuModule,
]);

@Component({
    selector: 'wdx-transactions',
    imports: [
        AgGridAngular,
        WdxAgGridThemeDirective,
        WdxSpinnerModule,
        WdxRadioButtonGroupModule,
        PortfolioNotLoadedComponent,
        PushPipe,
    ],
    providers: [PortfolioAgGridColumnMapperService, PortfolioConfigService],
    templateUrl: './transactions.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'h-100' },
})
export class TransactionsComponent implements OnInit {
    private portfolioConfigService = inject(PortfolioConfigService);
    private agGridColumnMapperService = inject(
        PortfolioAgGridColumnMapperService,
    );

    @Input() portfolioConfig?: InvestmentDataConfiguration;
    @Input() dataTypeLabel = 'Transaction';
    @Input() dataConnectionError?: FriendlyProblemDetails = undefined;
    @Input() localeCode?: string | null | undefined;
    @Input() set transactionsData(value: Transaction[] | undefined) {
        if (!value) {
            this.status = 'loading';
            return;
        }

        this.status = 'loaded';
        this.rowData = [...value];
        if (!this.rowData.length) {
            this.status = 'noData';
        }
    }
    @Input() includeTimeWithTransactionDates = true;
    @Input() transactionType: TransactionType = TransactionType.Security;

    public status: 'loading' | 'noData' | 'loaded' = 'loading';
    public rowData?: Transaction[];
    public checkedValue?: PerformancePeriod = PerformancePeriod.SinceInception;

    public detailSections$?: Observable<
        InvestmentDataSectionConfiguration[] | null | undefined
    >;
    public detailConfig$?: () => Observable<{
        sections?: InvestmentDataSectionConfiguration[] | null;
        fields?: InvestmentDataColumnConfiguration[];
    }> = () =>
        forkJoin({
            fields: this.portfolioConfigService
                .getExpandedColumns$(this.widgetName())
                .pipe(
                    map((fields) =>
                        fields && fields.length
                            ? fields
                            : TRANSACTION_DETAIL_DEFAULT_FIELDS,
                    ),
                ),
            sections: this.portfolioConfigService
                .getWidgetConfig$(this.widgetName())
                .pipe(map((config) => config?.sections ?? [])),
        });

    public gridOptions: GridOptions = {
        domLayout: 'autoHeight',
        pagination: true,
        paginationPageSize: 20,
        defaultColDef: {
            flex: 1,
            sortable: true,
            contextMenuItems: ['copy'],
        },
        detailCellRenderer: PortfolioDetailCellRendererComponent,
        detailCellRendererParams: {
            getConfig$: this.detailConfig$,
            updateDetailRowHeight: (rowNode: IRowNode, height: number) =>
                this.updateDetailRowHeight(rowNode, height),
        } as PortfolioDetailRendererParams,
        masterDetail: true,
        onGridReady: this.onGridReady.bind(this),
    };

    public colDefs$?: Observable<ColDef[]>;
    private gridApi?: GridApi;

    ngOnInit() {
        const lang = this.localeCode?.split('-')[0] || 'en';
        this.gridOptions.localeText =
            AG_GRID_LOCALES[lang] || AG_GRID_LOCALE_EN;

        const dateFieldName =
            this.transactionType === TransactionType.Account
                ? 'settlementDate'
                : 'transactionDate';

        this.colDefs$ = this.portfolioConfigService
            .getPrimaryColumns$(this.widgetName())
            .pipe(
                map((columns) =>
                    (columns.length
                        ? columns
                        : TRANSACTION_DEFAULT_PRIMARY_COLUMNS(dateFieldName)
                    )?.map((col) =>
                        this.agGridColumnMapperService.createColDef(col),
                    ),
                ),
                map((columns) => [
                    {
                        headerName: '',
                        flex: undefined,
                        width: 40,
                        maxWidth: 40,
                        cellRenderer: 'agGroupCellRenderer',
                        cellRendererParams: {
                            suppressCount: true,
                        },
                    },
                    ...columns,
                ]),
            );
    }

    public onGridReady(params: GridReadyEvent) {
        this.gridApi = params.api;
        this.portfolioConfigService
            .getWidgetConfig$(this.widgetName())
            .pipe(take(1))
            .subscribe((config) => {
                if (config?.defaultSort) {
                    const colId = (
                        this.gridApi
                            ?.getColumnDefs()
                            ?.find(
                                (col) =>
                                    (col as ColDef).field ===
                                    config.defaultSort?.path,
                            ) as ColDef
                    )?.colId;
                    if (colId) {
                        this.gridApi?.applyColumnState({
                            ...(colId
                                ? {
                                      state: [
                                          {
                                              colId,
                                              sort:
                                                  config.defaultSort
                                                      .sortDirection ===
                                                  SortDirection.Ascending
                                                      ? 'asc'
                                                      : 'desc',
                                          },
                                      ],
                                  }
                                : {}),
                            defaultState: { sort: null },
                        });
                    }
                }
            });
    }

    private widgetName(): PortfolioConfigName {
        return this.transactionType === TransactionType.Account
            ? PortfolioConfigName.PortfolioAccountTransactions
            : PortfolioConfigName.PortfolioTransactions;
    }

    private updateDetailRowHeight(rowNode: IRowNode, height: number): void {
        if (!rowNode || !height || !this.gridApi) {
            return;
        }
        rowNode.setRowHeight(height);
        this.gridApi?.onRowHeightChanged();
    }
}
